import { useCallback, useRef, useState } from "react";
import { useProfilePinEligibilityAPI } from "@/app/hooks/api_hooks/bookings/useProfilePinEligibilityAPI";

interface ICartPinGateRequest {
  userId: string;
  clinicId: string;
  userMobileNumber?: string;
  petId?: string;
  source: string;
}

interface ICartPinGateTarget extends ICartPinGateRequest {
  bookingId: string;
}

export const useCartProfilePinGate = () => {
  const { execute: fetchPinEligibility } = useProfilePinEligibilityAPI();
  const [pinTarget, setPinTarget] = useState<ICartPinGateTarget | null>(null);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);
  const proceedRef = useRef<(() => void) | null>(null);

  const runWithPinGate = useCallback(
    async (request: ICartPinGateRequest, proceed: () => void) => {
      if (isCheckingEligibility) return;
      if (!request.userId) {
        proceed();
        return;
      }
      setIsCheckingEligibility(true);
      const response = await fetchPinEligibility(request.userId);
      setIsCheckingEligibility(false);

      const bookingId = response.data?.booking_ids?.[0];
      if (!response.status || !response.data?.is_pin_eligible || !bookingId) {
        proceed();
        return;
      }
      proceedRef.current = proceed;
      setPinTarget({ ...request, bookingId });
    },
    [fetchPinEligibility, isCheckingEligibility],
  );

  const pinModalProps = {
    open: pinTarget !== null,
    setOpen: (value: boolean) => {
      if (!value) setPinTarget(null);
    },
    userId: pinTarget?.userId ?? "",
    clinicId: pinTarget?.clinicId ?? "",
    bookingId: pinTarget?.bookingId ?? "",
    userMobileNumber: pinTarget?.userMobileNumber,
    petId: pinTarget?.petId,
    source: pinTarget?.source ?? "",
    onVerified: () => {
      const proceed = proceedRef.current;
      proceedRef.current = null;
      proceed?.();
    },
  };

  return { runWithPinGate, isCheckingEligibility, pinModalProps };
};
